import { Controller } from '@hotwired/stimulus';

export default class extends Controller {
  static targets = ['input', 'dropzone', 'fileList', 'thumbnail', 'preview', 'emptyState'];
  static values = { uploadUrl: String, parentId: String, parent: String, name: String };

  connect() {
    document.addEventListener('dragover', this._preventDefault.bind(this));
    document.addEventListener('drop', this._preventDefault.bind(this));
    this._dragCounter = 0;
  }

  disconnect() {
    document.removeEventListener('dragover', this._preventDefault.bind(this));
    document.removeEventListener('drop', this._preventDefault.bind(this));
  }

  _preventDefault(event) {
    event.preventDefault();
    event.stopPropagation();
  }

  highlight(event) {
    event.preventDefault();
    this.dropzoneTarget.classList.add('border-blue-400', 'bg-blue-50');
  }

  unhighlight(event) {
    event.preventDefault();
    this.dropzoneTarget.classList.remove('border-blue-400', 'bg-blue-50');
  }

  handleDrop(event) {
    event.preventDefault();
    this.unhighlight(event);
    this._dragCounter = 0;
    this.dropzoneTarget.classList.add('hidden');

    const files = Array.from(event.dataTransfer.files);
    if (files.length === 0) return;

    this.inputTarget.files = event.dataTransfer.files;
    this._processFiles(files);
  }

  handleChoose(event) {
    const file = event.target.files[0];
    if (!file) return;
    this._processFiles([file]);
  }

  triggerReplace() {
    this.inputTarget.click();
  }

  showDropzone() {
    this._dragCounter++;
    this.dropzoneTarget.classList.remove('hidden');
  }

  hideDropzone(event) {
    this._dragCounter--;
    if (this._dragCounter <= 0) {
      this._dragCounter = 0;
      this.dropzoneTarget.classList.add('hidden');
    }
  }

  _processFiles(files) {
    if (this.hasUploadUrlValue) {
      this._uploadFiles(files);
    } else {
      this._previewFile(files[0]);
    }
  }

  _uploadFiles(files) {
    for (let i = 0; i < files.length; i++) {
      this._handleFile(files[i]);
    }
  }

  _handleFile(file) {
    let id = '' + file.lastModified + file.size;
    let reader = new FileReader();

    reader.onloadstart = () => this._loadstart(file, id);
    reader.onload = () => this._load(file, id);

    reader.readAsBinaryString(file);
  }

  _loadstart(file, id) {
    this._showProgressTemplate(file, id);
  }

  _load(file, id) {
    let event = new CustomEvent('file-uploader:upload', { detail: { file: file, id: id } });
    this.element.dispatchEvent(event);
    this._performFileUpload(file, id);
  }

  _performFileUpload(file, id) {
    let formData = new FormData();
    formData.append('data', file);
    formData.append('form[parent_id]', this.parentIdValue || '');
    formData.append('form[parent]', this.parentValue || '');
    formData.append('form[name]', this.nameValue || '');

    let handleProgress = this._progress(id);
    let handleSuccess = this._success(id);
    let handleError = this._error(id);

    const xhr = new XMLHttpRequest();
    xhr.open('POST', this.uploadUrlValue, true);

    xhr.setRequestHeader('Accept', 'text/html, application/json');
    xhr.setRequestHeader('X-Requested-With', 'XMLHttpRequest');
    xhr.setRequestHeader('X-CSRF-Token', this._metaValue('csrf-token'));
    xhr.setRequestHeader('Access-Control-Allow-Origin', location.origin);

    xhr.upload.addEventListener('progress', handleProgress);
    xhr.addEventListener('progress', handleProgress);

    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        handleSuccess(xhr.responseText, xhr.status);
      } else {
        handleError(xhr, xhr.status, xhr.statusText);
      }
    };

    xhr.onerror = () => handleError(xhr, xhr.status, xhr.statusText);

    xhr.send(formData);
  }

  _showProgressTemplate(file, id) {
    const progressHTML = `
      <div class="flex items-center gap-12 py-12 px-4 bg-black-25 rounded-lg mb-2" data-id="${id}">
        <div class="flex-1">
          <div class="text-sm font-medium text-black-800">${file.name}</div>
          <div class="w-full bg-black-200 rounded-full h-2 mt-4">
            <div class="bg-primary-500 h-2 rounded-full transition-all duration-300" style="width: 0%"></div>
          </div>
        </div>
      </div>
    `;
    this.fileListTarget.insertAdjacentHTML('afterbegin', progressHTML);
  }

  _progress(id) {
    return function (e) {
      if (e.lengthComputable) {
        let percent = (e.loaded / e.total) * 100;
        const progressBar = document.querySelector(`[data-id="${id}"] .bg-primary-500`);
        if (progressBar) {
          progressBar.style.width = percent + '%';
        }
      }
    };
  }

  _success(id) {
    return function () {
      const progressElement = document.querySelector(`[data-id="${id}"]`);
      if (progressElement) {
        progressElement.remove();
      }
      location.reload();
    };
  }

  _error(id) {
    return function (xhr, status, error) {
      const progressElement = document.querySelector(`[data-id="${id}"]`);
      if (progressElement) {
        progressElement.innerHTML = `
          <div class="flex items-center gap-12 py-12 px-4 bg-red-50 border border-red-200 rounded-lg text-red-600">
            <span class="text-sm">Error uploading file: ${error}</span>
          </div>
        `;
      }
    };
  }

  _metaValue(name) {
    let element = document.querySelector(`meta[name="${name}"]`);
    if (element) {
      return element.getAttribute('content');
    }
  }

  _previewFile(file) {
    if (!file.type.startsWith('image/')) {
      alert('Please upload an image file.');
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      if (this.hasThumbnailTarget) {
        this.thumbnailTarget.src = reader.result;
      }
      this._showPreview();
    };
    reader.readAsDataURL(file);
  }

  _showPreview() {
    if (this.hasPreviewTarget) {
      this.previewTarget.classList.remove('hidden');
    }
    this.dropzoneTarget.classList.add('hidden');
  }

  _clearPreview() {
    if (this.hasPreviewTarget) {
      this.previewTarget.classList.add('hidden');
    }
    this.dropzoneTarget.classList.remove('hidden');
    if (this.hasThumbnailTarget) {
      this.thumbnailTarget.src = '#';
    }
  }
}
